import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  MapPin, Building2, Calendar, Video, Loader2, RefreshCw,
  Film, CheckCircle, Clapperboard, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { VideosModal } from './BranchListings';
import './EditorListings.css';

const API = process.env.REACT_APP_BACKEND_URL;

export const openClipsInEditor = (orderId, { productFilter, productName, excludeNames, orderedNames } = {}, onNavigate) => {
  try {
    sessionStorage.setItem('prefillWmsJobId', String(orderId));
    if (productFilter) sessionStorage.setItem('prefillWmsProductFilter', productFilter);
    else sessionStorage.removeItem('prefillWmsProductFilter');
    if (productName) sessionStorage.setItem('prefillWmsProductLabel', productName);
    else sessionStorage.removeItem('prefillWmsProductLabel');
    if (excludeNames && excludeNames.length) sessionStorage.setItem('prefillWmsExcludeNames', JSON.stringify(excludeNames));
    else sessionStorage.removeItem('prefillWmsExcludeNames');
    if (orderedNames && orderedNames.length) sessionStorage.setItem('prefillWmsOrderedNames', JSON.stringify(orderedNames));
    else sessionStorage.removeItem('prefillWmsOrderedNames');
  } catch (_) {}
  if (onNavigate) onNavigate('video');
};

export const formatListingDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export function AssignmentCard({ listing, onShowVideos, delay = 0 }) {
  const vids = listing.video_products || [];
  const isDone = listing.status === 'complete';
  return (
    <div
      className={`el-card ${isDone ? 'el-card-done' : ''}`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`el-card-${listing.order_id}`}
    >
      <div className="el-card-top">
        <span className="el-order-id">#{listing.order_id}</span>
        <span className={`el-status-pill ${isDone ? 'el-pill-done' : 'el-pill-edit'}`}>
          {isDone ? <><CheckCircle size={11} /> Complete</> : <><Clapperboard size={11} /> To Edit</>}
        </span>
      </div>
      <h4 className="el-address"><MapPin size={13} /> {listing.full_address || 'Address unavailable'}</h4>
      <div className="el-meta">
        {listing.client_name && <span><Building2 size={12} /> {listing.client_name}</span>}
        {listing.branch_name && <span className="el-branch">{listing.branch_name}</span>}
        {listing.created_at && <span><Calendar size={12} /> {formatListingDate(listing.created_at)}</span>}
      </div>
      <div className="el-products">
        {vids.map((v, i) => (
          <span key={i} className={`el-product-badge ${(v.progress_code || 0) >= 400 ? 'el-badge-done' : ''}`}>
            <Film size={11} /> {v.name}
          </span>
        ))}
      </div>
      <button
        className="el-video-btn"
        onClick={onShowVideos}
        data-testid={`el-video-btn-${listing.order_id}`}
      >
        <Video size={15} /> View Videos
      </button>
    </div>
  );
}

export default function EditorListings({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videosModal, setVideosModal] = useState(null);
  const [filter, setFilter] = useState('to_edit');
  const pollRef = useRef(null);
  const pollCount = useRef(0);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/api/editor-hub/my-listings`);
      setData(res.data);
      clearTimeout(pollRef.current);
      if (res.data.syncing && pollCount.current < 30) {
        pollCount.current += 1;
        pollRef.current = setTimeout(() => load(true), 4000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load your listings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(pollRef.current);
  }, [load]);

  const refresh = async () => {
    try {
      await axios.post(`${API}/api/editor-hub/refresh`);
      toast.success('Syncing with WMS…');
      setTimeout(() => load(true), 1500);
    } catch {
      toast.error('Refresh failed');
    }
  };

  const listings = data?.listings || [];
  const toEdit = listings.filter(l => l.status === 'to_edit');
  const complete = listings.filter(l => l.status === 'complete');
  const visible = filter === 'to_edit' ? toEdit : filter === 'complete' ? complete : listings;
  const syncing = data?.syncing;

  if (loading) {
    return <div className="el-loading"><Loader2 size={28} className="animate-spin" /><span>Loading your listings…</span></div>;
  }

  if (data && !data.wms_editor_id) {
    return (
      <div className="el-empty" data-testid="el-no-wms-link">
        <AlertCircle size={28} />
        <span>Your account isn't linked to a WMS editor profile.</span>
        <small>Ask an admin to link your WMS user ID.</small>
      </div>
    );
  }

  return (
    <div className="editor-listings" data-testid="editor-my-listings">
      <div className="el-toolbar">
        <div className="el-filter-pills">
          <button className={`el-pill ${filter === 'to_edit' ? 'is-active' : ''}`} onClick={() => setFilter('to_edit')} data-testid="el-filter-toedit">
            To Edit <span className="el-pill-count">{toEdit.length}</span>
          </button>
          <button className={`el-pill ${filter === 'complete' ? 'is-active' : ''}`} onClick={() => setFilter('complete')} data-testid="el-filter-complete">
            Completed <span className="el-pill-count">{complete.length}</span>
          </button>
          <button className={`el-pill ${filter === 'all' ? 'is-active' : ''}`} onClick={() => setFilter('all')} data-testid="el-filter-all">
            All <span className="el-pill-count">{listings.length}</span>
          </button>
        </div>
        <div className="el-toolbar-right">
          {syncing && <span className="el-syncing" data-testid="el-syncing"><Loader2 size={13} className="animate-spin" /> Syncing WMS…</span>}
          <button className="el-refresh-btn" onClick={refresh} data-testid="el-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="el-empty" data-testid="el-empty">
          <Film size={30} />
          <span>{syncing ? 'Pulling your assigned orders from WMS…' : filter === 'to_edit' ? 'No orders assigned to you right now.' : 'Nothing here yet.'}</span>
          <small>Orders assigned to you in WMS appear here automatically.</small>
        </div>
      ) : (
        <div className="el-grid">
          {visible.map((l, i) => (
            <AssignmentCard
              key={l.order_id}
              listing={l}
              delay={Math.min(i * 40, 400)}
              onShowVideos={() => setVideosModal({ listing: l })}
            />
          ))}
        </div>
      )}

      {videosModal && (
        <VideosModal
          listing={videosModal.listing}
          onClose={() => setVideosModal(null)}
          onOpenEditor={(opts) => {
            openClipsInEditor(videosModal.listing.order_id, opts, onNavigate);
            setVideosModal(null);
          }}
        />
      )}
    </div>
  );
}
